import React, { createContext, useContext, useCallback, useEffect, useState, useRef } from 'react';
import useWhatsappSessionId from '../../hooks/useWhatsappSessionId';
import WhatsappSessionsService from '../../services/whatsappSessions';

// Context que expone sessionId seleccionado y helpers para operar multi-sesión.
// Mantiene compatibilidad: si no hay sessionId se pueden seguir usando endpoints legacy afuera.

const WhatsappSessionsContext = createContext(null);

export const WhatsappSessionsProvider = ({ children, autoRecreate = false, onRecreate }) => {
  const { sessionId, setSessionId } = useWhatsappSessionId();
  const [sessionExists, setSessionExists] = useState(true); // asumimos true hasta verificar
  const verifyingRef = useRef(false);
  const initialHydratedRef = useRef(false);
  const [initializing, setInitializing] = useState(true); // indica que aún estamos resolviendo restauración/verify inicial
  // Eliminamos lógica de restauración por listado; sólo usamos localStorage + verify.

  useEffect(() => {
    if (!initialHydratedRef.current) {
      console.log('[WhatsappSessionsProvider] Hydrated sessionId from localStorage:', sessionId);
      initialHydratedRef.current = true;
    } else {
      console.log('[WhatsappSessionsProvider] sessionId changed:', sessionId);
    }
  }, [sessionId]);

  const selectSession = useCallback((id) => {
    console.log('[WhatsappSessionsProvider] selectSession called with id:', id);
    setSessionId(id);
  }, [setSessionId]);

  const createSession = useCallback(async () => {
    const created = await WhatsappSessionsService.createSession();
    const newId = created?.sessionId || created?.id;
    if (newId) selectSession(newId);
    console.log('[WhatsappSessionsProvider] createSession result id:', newId);
    return created;
  }, [selectSession]);

  const deleteSession = useCallback(async (id) => {
    await WhatsappSessionsService.deleteSession(id);
    if (sessionId === id) selectSession(null);
  }, [sessionId, selectSession]);

  const verifySession = useCallback(async () => {
    if (!sessionId) {
      setSessionExists(false);
      console.log('[WhatsappSessionsProvider] verifySession: no sessionId present');
      return false;
    }
    try {
      verifyingRef.current = true;
      console.log('[WhatsappSessionsProvider] verifySession: requesting status for', sessionId);
      await WhatsappSessionsService.getStatus(sessionId);
      setSessionExists(true);
      console.log('[WhatsappSessionsProvider] verifySession: session exists');
      return true;
    } catch (e) {
      const statusCode = e?.response?.status || e?.status || e?.code;
      if (statusCode === 404) {
        // Sesión desapareció en backend (reinicio de servidor)
        setSessionExists(false);
        console.warn('[WhatsappSessionsProvider] verifySession: session not found (404)');
        return false;
      }
      // Otros errores: no confirmamos inexistencia
      console.warn('[WhatsappSessionsProvider] verifySession: non-404 error, assuming exists', e?.message);
      return true;
    } finally {
      verifyingRef.current = false;
    }
  }, [sessionId]);

  const verifyOrRecreate = useCallback(async () => {
    const exists = await verifySession();
    if (!exists && autoRecreate) {
      const created = await createSession();
      onRecreate?.(created?.sessionId || created?.id);
      return true;
    }
    return exists;
  }, [verifySession, autoRecreate, createSession, onRecreate]);

  // Verificar al montar o cuando cambia sessionId; si no hay sessionId intentar seleccionar uno existente.
  useEffect(() => {
    const run = async () => {
      if (sessionId) {
        console.log('[WhatsappSessionsProvider] init: sessionId presente, verificando');
        await verifySession();
        setInitializing(false);
      } else {
        console.log('[WhatsappSessionsProvider] init: sin sessionId en localStorage');
        setSessionExists(false);
        setInitializing(false);
      }
    };
    run();
  }, [sessionId, verifySession]);

  const value = {
    sessionId,
    sessionExists,
    initializing,
    selectSession,
    createSession,
    deleteSession,
    verifySession,
    verifyOrRecreate,
  };

  return (
    <WhatsappSessionsContext.Provider value={value}>
      {children}
    </WhatsappSessionsContext.Provider>
  );
};

export const useWhatsappSessions = () => {
  const ctx = useContext(WhatsappSessionsContext);
  if (!ctx) throw new Error('useWhatsappSessions debe usarse dentro de WhatsappSessionsProvider');
  return ctx;
};
